export class ApiClientError extends Error {
    kind;
    /** Set for kind 'http' — the admin console switches on 403 constantly. */
    status;
    /** Set for kind 'parse'. */
    issues;
    constructor(kind, message, opts) {
        super(message);
        this.name = 'ApiClientError';
        this.kind = kind;
        if (opts?.status !== undefined)
            this.status = opts.status;
        if (opts?.issues !== undefined)
            this.issues = opts.issues;
    }
}
function buildUrl(baseUrl, path, params, query) {
    let resolved = path.replace(/:([A-Za-z0-9_]+)/g, (_, name) => {
        const value = params?.[name];
        if (value === undefined)
            throw new ApiClientError('parse', `Missing path param :${name}`);
        return encodeURIComponent(value);
    });
    if (query) {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v === undefined || v === null)
                continue;
            // An array becomes REPEATED parameters (`?a=x&a=y`), which is what the
            // server's parser reads back as an array. `String(v)` would join it with
            // commas into a single value instead.
            //
            // Every array-valued query contract must therefore accept an array. That
            // is not automatic: `IdListQuery` once took only a comma-separated
            // string, so every batched author and viewer lookup answered 400 while
            // both sides looked correct on their own. `id-list-query.test.ts` and the
            // round-trip test in this package's suite exist to keep the two ends
            // talking about the same wire.
            if (Array.isArray(v)) {
                for (const item of v) {
                    if (item !== undefined && item !== null)
                        qs.append(k, String(item));
                }
            }
            else {
                qs.set(k, String(v));
            }
        }
        const s = qs.toString();
        if (s)
            resolved += `?${s}`;
    }
    return `${baseUrl.replace(/\/$/, '')}${resolved}`;
}
/**
 * Builds the `multipart/form-data` body for an upload route: the file under
 * the field name the contract names, plus its text fields — validated against
 * the contract's `fields` schema first, so a bad duration or folder fails here
 * rather than after the bytes have crossed the wire.
 */
function uploadBody(upload, args) {
    const Ctor = globalThis.FormData;
    if (!Ctor)
        throw new ApiClientError('parse', 'This runtime has no FormData');
    if (!args?.file)
        throw new ApiClientError('parse', `Missing file part "${upload.field}"`);
    const form = new Ctor();
    /*
      Text parts FIRST, file last — the order is part of the protocol here, not
      a style choice.
  
      Multipart is a stream, and the server reads its text fields at the moment
      the file part arrives: anything sent after the file is still on the wire.
      With a small file it usually lands in time and appears to work; with a real
      photo it does not, and the field is silently missing. That asymmetry is
      what made a 160-byte test upload keep its folder while a 6MB one lost it.
  
      `durationSeconds` on a video upload is required, so the same ordering is
      the difference between a working reel upload and a 400.
    */
    if (upload.fields) {
        const parsed = upload.fields.safeParse(args.fields ?? {});
        if (!parsed.success) {
            throw new ApiClientError('parse', 'Upload fields violate the contract', {
                issues: parsed.error,
            });
        }
        for (const [k, v] of Object.entries(parsed.data)) {
            if (v !== undefined && v !== null)
                form.append(k, String(v));
        }
    }
    form.append(upload.field, args.file);
    return form;
}
export function createClient(manifest, config) {
    const doFetch = config.fetch ?? globalThis.fetch;
    async function call(route, args, retried = false) {
        const headers = {};
        const token = await config.getToken?.();
        if (token)
            headers.authorization = `Bearer ${token}`;
        // Validate the request body BEFORE sending — a client-side contract
        // violation should never reach the network.
        //
        // `content-type` is set only where there is a body to describe. Sending
        // `application/json` with nothing after it is a lie about the request, and
        // servers are entitled to reject it — ours does, with a 400 raised in the
        // body parser, i.e. BEFORE the auth guard runs. That made every
        // bodyless write (favourite, block, comment-like) fail while the same
        // route worked from curl, which sends a different content type.
        let body;
        if (route.upload) {
            // No content-type here either: the boundary is generated by fetch, and a
            // hand-set header loses it, so every part arrives as one unparsable blob.
            body = uploadBody(route.upload, args);
        }
        else if (route.body) {
            const parsed = route.body.safeParse(args?.body);
            if (!parsed.success) {
                throw new ApiClientError('parse', 'Request body violates the contract', {
                    issues: parsed.error,
                });
            }
            body = JSON.stringify(parsed.data);
            headers['content-type'] = 'application/json';
        }
        /*
          Validated with the schema, but serialised from what the CALLER passed.
    
          A query schema may transform on the way in — `IdListQuery` turns the
          string "a,b" into an array so handlers receive a list — and the parsed
          output is the server's shape, not the wire's. Serialising that instead of
          the input sent `?ids=a&ids=b` for a field the server reads as a string,
          which came back a 400 and left every feed card without an author name.
    
          Parsed values still supply defaults for keys the caller omitted; the
          caller's own values win wherever they gave one.
        */
        const query = route.query && args?.query
            ? {
                ...route.query.parse(args.query),
                ...args.query,
            }
            : undefined;
        const url = buildUrl(config.baseUrl, route.path, args?.params, query);
        let res;
        try {
            res = await doFetch(url, { method: route.method, headers, ...(body ? { body } : {}) });
        }
        catch (e) {
            throw new ApiClientError('network', e instanceof Error ? e.message : 'Network failure');
        }
        if (res.status === 401 && !retried && config.onUnauthorized) {
            const refreshed = await config.onUnauthorized({ method: route.method, path: route.path });
            if (refreshed)
                return call(route, args, true);
        }
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new ApiClientError('http', text || `HTTP ${res.status}`, { status: res.status });
        }
        const raw = await res.json();
        const parsed = route.response.safeParse(raw);
        if (!parsed.success) {
            // The server sent something the contract does not describe. Failing here
            // is the point: silent acceptance is how an unfiltered feed ships.
            throw new ApiClientError('parse', 'Response violates the contract', {
                issues: parsed.error,
            });
        }
        return parsed.data;
    }
    const client = {};
    for (const [group, groupRoutes] of Object.entries(manifest)) {
        client[group] = {};
        for (const [name, route] of Object.entries(groupRoutes)) {
            client[group][name] = (args) => call(route, args);
        }
    }
    return client;
}
//# sourceMappingURL=client.js.map